
import { useState, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchReagents } from "@/services/reagents/fetchReagents";
import { fetchCategories } from "@/services/categoryService";
import { useAuth } from "@/contexts/auth";
import { useReagentCache } from "./reagents/useReagentCache";
import { useReagentLoadingState } from "./reagents/useReagentLoadingState";
import { useReagentRefresh } from "./reagents/useReagentRefresh";
import { useReagentRetry } from "./reagents/useReagentRetry";
import { useReagentAuthEffect } from "./reagents/useReagentAuthEffect";

export const useReagentsQuery = () => {
  const queryClient = useQueryClient();
  const { user, isLoading: authLoading } = useAuth();
  const [isRetrying, setIsRetrying] = useState(false);
  const [hasLoggedStatus, setHasLoggedStatus] = useState(false);

  // Use the cache management hook
  useReagentCache(user?.id, []);

  // Fetch reagents data
  const { 
    data: reagents = [], 
    isLoading: loadingReagents,
    error: reagentsError,
    refetch: refetchReagents,
    isError,
    failureCount,
    isFetching,
    status
  } = useQuery({
    queryKey: ['reagents'],
    queryFn: fetchReagents,
    staleTime: 1000 * 60 * 5, // Increase to 5 minutes to reduce refreshing
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (attempt) => Math.min(attempt > 1 ? 3000 : 1000, 30 * 1000),
    gcTime: 1000 * 60 * 10,
    enabled: !authLoading && !!user,
  });

  // Fetch categories data
  const { 
    data: categories = [], 
    isLoading: loadingCategories 
  } = useQuery({
    queryKey: ['categories'],
    queryFn: fetchCategories,
    staleTime: 1000 * 60 * 30, // 30 minutes
    refetchOnWindowFocus: false,
    enabled: !authLoading && !!user,
  });

  // Use the loading state hook
  const { forceLoadComplete, isActuallyLoading } = useReagentLoadingState(
    loadingReagents,
    isFetching,
    authLoading,
    isRetrying,
    reagents
  );

  // Use the retry logic hook
  useReagentRetry(
    loadingReagents,
    isFetching,
    authLoading,
    user,
    reagents,
    isError,
    refetchReagents,
    setIsRetrying
  );

  // Use the auth effect hook
  useReagentAuthEffect(authLoading, user, refetchReagents);

  // Use the manual refresh hook
  const { handleManualRefresh } = useReagentRefresh(refetchReagents);

  // Log query status changes once per session, not continuously
  useEffect(() => {
    if (!hasLoggedStatus && status === 'success' && reagents.length > 0) {
      console.log(`Successfully loaded ${reagents.length} reagents`);
      setHasLoggedStatus(true);
    }
  }, [status, reagents.length, hasLoggedStatus]);

  return {
    // Data
    reagents,
    categories,
    
    // Loading states
    isLoading: isActuallyLoading,
    loadingCategories,
    
    // Error states
    error: reagentsError as Error | null,
    isError,
    
    // Actions
    refetch: handleManualRefresh,
    isRetrying
  };
};
